import { useState } from 'react';
import {
  Search,
  Download,
  Code,
  Eye,
  ChevronDown,
  ChevronRight,
  Clock,
  Zap,
  Database,
  AlertCircle,
  CheckCircle,
  DollarSign,
} from 'lucide-react';
import { Breadcrumb } from '@/components/Breadcrumb';
import { Tooltip } from '@/components/Tooltip';
import { StatusBadge } from '@/components/Badge';
import { EmptyState } from '@/components/EmptyState';

interface TraceNode {
  id: string;
  type: 'stage' | 'server' | 'decision' | 'api_call' | 'validation';
  name: string;
  status: 'success' | 'error' | 'warning';
  timestamp: string;
  duration?: number;
  data?: any;
  children?: TraceNode[];
}

export function TraceExplorer() {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTrace, setSelectedTrace] = useState<string | null>('trace_abc123');
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set(['root']));
  const [viewMode, setViewMode] = useState<'timeline' | 'tree' | 'raw'>('tree');
  const [showDataDialog, setShowDataDialog] = useState(false);
  const [selectedNodeData, setSelectedNodeData] = useState<any>(null);

  const traces = [
    {
      traceId: 'trace_abc123',
      exam: 'CPA-10',
      oeCode: 'OE.01.01',
      questionType: 'multiple_choice',
      status: 'success',
      timestamp: '2026-01-07 10:30:45',
      duration: 5120,
      tokensUsed: 6500,
      cost: 0.016,
      questionsGenerated: 3,
    },
    {
      traceId: 'trace_def456',
      exam: 'CPA-20',
      oeCode: 'OE.02.03',
      questionType: 'true_false',
      status: 'error',
      timestamp: '2026-01-07 10:28:12',
      duration: 2450,
      tokensUsed: 1200,
      cost: 0.004,
      questionsGenerated: 0,
    },
    {
      traceId: 'trace_ghi789',
      exam: 'CEA',
      oeCode: 'OE.03.01',
      questionType: 'multiple_choice',
      status: 'success',
      timestamp: '2026-01-07 10:25:33',
      duration: 4820,
      tokensUsed: 6800,
      cost: 0.018,
      questionsGenerated: 10,
    },
  ];

  const traceData: TraceNode = {
    id: 'root',
    type: 'stage',
    name: 'Agentic RAG Pipeline',
    status: 'success',
    timestamp: '2026-01-07T10:30:45.123Z',
    duration: 5120,
    children: [
      {
        id: 'query-analysis',
        type: 'stage',
        name: '1. Query Analyzer (Claude Sonnet 4.5)',
        status: 'success',
        timestamp: '2026-01-07T10:30:45.150Z',
        duration: 780,
        data: {
          input: {
            query: 'OE.01.01 - Sistema Financeiro Nacional',
            exam: 'CPA-10',
            difficulty: 'medium',
          },
          output: {
            intent: 'question_generation',
            entities: {
              oeCode: 'OE.01.01',
              topic: 'Sistema Financeiro Nacional',
              concepts: ['Banco Central', 'CVM', 'CMN'],
            },
            recommendedServers: ['mcp-oe-database', 'mcp-regulations', 'mcp-pinecone', 'mcp-previous-exams'],
            confidence: 0.92,
          },
          tokens: 450,
          cost: 0.0014,
        },
        children: [
          {
            id: 'query-analysis-api',
            type: 'api_call',
            name: 'Claude API Call',
            status: 'success',
            timestamp: '2026-01-07T10:30:45.160Z',
            duration: 750,
            data: {
              model: 'claude-sonnet-4.6-20250929',
              temperature: 0.3,
              maxTokens: 1000,
              tokensUsed: 450,
            },
          },
        ],
      },
      {
        id: 'mcp-router',
        type: 'stage',
        name: '2. MCP Router (7 Parallel Servers)',
        status: 'success',
        timestamp: '2026-01-07T10:30:45.930Z',
        duration: 1450,
        data: {
          serversQueried: 7,
          totalChunks: 45,
          avgLatency: 207,
        },
        children: [
          {
            id: 'mcp-pinecone',
            type: 'server',
            name: 'MCP Pinecone (Vector Search)',
            status: 'success',
            timestamp: '2026-01-07T10:30:45.940Z',
            duration: 210,
            data: {
              query: 'estrutura sistema financeiro nacional autoridades',
              topK: 10,
              chunksReturned: 10,
              avgScore: 0.88,
            },
          },
          {
            id: 'mcp-oe-database',
            type: 'server',
            name: 'MCP OE Database',
            status: 'success',
            timestamp: '2026-01-07T10:30:45.940Z',
            duration: 95,
            data: {
              oeCode: 'OE.01.01',
              depth: 2,
              chunksReturned: 3,
            },
          },
          {
            id: 'mcp-regulations',
            type: 'server',
            name: 'MCP Regulations',
            status: 'success',
            timestamp: '2026-01-07T10:30:45.940Z',
            duration: 315,
            data: {
              topics: ['Banco Central', 'CVM', 'CMN'],
              regulations: ['Lei 4595/1964', 'Lei 6385/1976'],
              chunksReturned: 8,
            },
          },
          {
            id: 'mcp-previous-exams',
            type: 'server',
            name: 'MCP Previous Exams',
            status: 'success',
            timestamp: '2026-01-07T10:30:45.940Z',
            duration: 145,
            data: {
              exam: 'CPA-10',
              oeCode: 'OE.01.01',
              chunksReturned: 6,
            },
          },
          {
            id: 'mcp-glossary',
            type: 'server',
            name: 'MCP Glossary',
            status: 'success',
            timestamp: '2026-01-07T10:30:45.940Z',
            duration: 82,
            data: {
              terms: ['Sistema Financeiro Nacional', 'SPB', 'CMN'],
              chunksReturned: 4,
            },
          },
          {
            id: 'mcp-pdf-content',
            type: 'server',
            name: 'MCP PDF Content',
            status: 'success',
            timestamp: '2026-01-07T10:30:45.940Z',
            duration: 178,
            data: {
              documents: ['CPA10_Syllabus.pdf'],
              chunksReturned: 7,
            },
          },
          {
            id: 'mcp-anbima-api',
            type: 'server',
            name: 'MCP ANBIMA API',
            status: 'success',
            timestamp: '2026-01-07T10:30:45.940Z',
            duration: 425,
            data: {
              officialQuestions: true,
              chunksReturned: 7,
            },
          },
        ],
      },
      {
        id: 'reranker',
        type: 'stage',
        name: '3. Reranker (Hybrid + Cohere)',
        status: 'success',
        timestamp: '2026-01-07T10:30:47.380Z',
        duration: 590,
        data: {
          inputChunks: 45,
          hybridScoring: {
            semanticWeight: 0.4,
            oeRelevanceWeight: 0.3,
            sourceWeight: 0.2,
            recencyWeight: 0.1,
          },
          cohereModel: 'rerank-multilingual-v3.0',
          outputChunks: 15,
          deduplication: {
            threshold: 0.9,
            removed: 3,
          },
        },
        children: [
          {
            id: 'cohere-api',
            type: 'api_call',
            name: 'Cohere Rerank API',
            status: 'success',
            timestamp: '2026-01-07T10:30:47.500Z',
            duration: 380,
            data: {
              model: 'rerank-multilingual-v3.0',
              documents: 45,
              topN: 20,
              tokensUsed: 2000,
              cost: 0.0020,
            },
          },
        ],
      },
      {
        id: 'llm-generation',
        type: 'stage',
        name: '4. LLM Generation (Claude Sonnet 4.5)',
        status: 'success',
        timestamp: '2026-01-07T10:30:47.970Z',
        duration: 2050,
        data: {
          contextTokens: 2500,
          generationTokens: 3000,
          totalTokens: 5500,
          cost: 0.0090,
          questionsGenerated: 3,
        },
        children: [
          {
            id: 'context-merger',
            type: 'decision',
            name: 'Context Merger',
            status: 'success',
            timestamp: '2026-01-07T10:30:47.980Z',
            duration: 100,
            data: {
              sourceSummary: 'Retrieved from 5 sources: mcp-regulations (8), mcp-pinecone (10), ...',
              formattedChunks: 15,
              totalTokens: 2500,
            },
          },
          {
            id: 'llm-api',
            type: 'api_call',
            name: 'Claude API Call',
            status: 'success',
            timestamp: '2026-01-07T10:30:48.080Z',
            duration: 1950,
            data: {
              model: 'claude-sonnet-4.6-20250929',
              temperature: 0.7,
              maxTokens: 4000,
              promptTokens: 2500,
              completionTokens: 3000,
            },
          },
        ],
      },
      {
        id: 'validation',
        type: 'stage',
        name: '5. 8-Layer Validation',
        status: 'success',
        timestamp: '2026-01-07T10:30:50.020Z',
        duration: 300,
        data: {
          overallValid: true,
          passedLayers: 8,
          failedLayers: 0,
          avgConfidence: 0.94,
        },
        children: [
          {
            id: 'validation-technical',
            type: 'validation',
            name: 'Layer 1: Technical Quality',
            status: 'success',
            timestamp: '2026-01-07T10:30:50.030Z',
            duration: 35,
            data: { passed: true, confidence: 0.97 },
          },
          {
            id: 'validation-factual',
            type: 'validation',
            name: 'Layer 2: Factual Accuracy',
            status: 'success',
            timestamp: '2026-01-07T10:30:50.065Z',
            duration: 42,
            data: { passed: true, confidence: 0.95 },
          },
          {
            id: 'validation-oe',
            type: 'validation',
            name: 'Layer 3: OE Alignment',
            status: 'success',
            timestamp: '2026-01-07T10:30:50.107Z',
            duration: 38,
            data: { passed: true, confidence: 0.93 },
          },
          {
            id: 'validation-difficulty',
            type: 'validation',
            name: 'Layer 4: Difficulty Calibration',
            status: 'success',
            timestamp: '2026-01-07T10:30:50.145Z',
            duration: 40,
            data: { passed: true, confidence: 0.91 },
          },
          {
            id: 'validation-clarity',
            type: 'validation',
            name: 'Layer 5: Clarity',
            status: 'success',
            timestamp: '2026-01-07T10:30:50.185Z',
            duration: 36,
            data: { passed: true, confidence: 0.96 },
          },
          {
            id: 'validation-distractors',
            type: 'validation',
            name: 'Layer 6: Distractor Quality',
            status: 'success',
            timestamp: '2026-01-07T10:30:50.221Z',
            duration: 45,
            data: { passed: true, confidence: 0.89 },
          },
          {
            id: 'validation-ethics',
            type: 'validation',
            name: 'Layer 7: Ethical Compliance',
            status: 'success',
            timestamp: '2026-01-07T10:30:50.266Z',
            duration: 28,
            data: { passed: true, confidence: 0.99 },
          },
          {
            id: 'validation-edge',
            type: 'validation',
            name: 'Layer 8: Edge Cases',
            status: 'success',
            timestamp: '2026-01-07T10:30:50.294Z',
            duration: 36,
            data: { passed: true, confidence: 0.92 },
          },
        ],
      },
    ],
  };

  const filteredTraces = traces.filter(trace =>
    searchTerm === '' ||
    trace.traceId.toLowerCase().includes(searchTerm.toLowerCase()) ||
    trace.exam.toLowerCase().includes(searchTerm.toLowerCase()) ||
    trace.oeCode.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const selectedTraceData = traces.find(t => t.traceId === selectedTrace);

  const handleDownloadTrace = () => {
    if (!selectedTrace) return;
    const jsonContent = JSON.stringify(traceData, null, 2);
    const blob = new Blob([jsonContent], { type: 'application/json' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `trace-${selectedTrace}.json`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const toggleNode = (nodeId: string) => {
    const newExpanded = new Set(expandedNodes);
    if (newExpanded.has(nodeId)) {
      newExpanded.delete(nodeId);
    } else {
      newExpanded.add(nodeId);
    }
    setExpandedNodes(newExpanded);
  };

  const getNodeIcon = (type: string, status: string) => {
    if (status === 'error') return <AlertCircle className="w-4 h-4 text-red-600" />;
    if (status === 'warning') return <AlertCircle className="w-4 h-4 text-yellow-600" />;

    switch (type) {
      case 'stage':
        return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'server':
        return <Database className="w-4 h-4 text-blue-600" />;
      case 'api_call':
        return <Zap className="w-4 h-4 text-#ea7500" />;
      case 'validation':
        return <CheckCircle className="w-4 h-4 text-green-600" />;
      default:
        return <CheckCircle className="w-4 h-4 text-gray-600" />;
    }
  };

  const renderTreeNode = (node: TraceNode, level: number = 0) => {
    const isExpanded = expandedNodes.has(node.id);
    const hasChildren = node.children && node.children.length > 0;

    return (
      <div key={node.id} style={{ marginLeft: `${level * 24}px` }}>
        <div
          className={`flex items-start space-x-3 p-3 rounded-lg hover:bg-gray-50 cursor-pointer ${
            level === 0 ? 'bg-gray-50' : ''
          }`}
          onClick={() => hasChildren && toggleNode(node.id)}
        >
          {hasChildren && (
            <Tooltip content={isExpanded ? "Collapse" : "Expand"}>
              <button className="flex-shrink-0">
                {isExpanded ? (
                  <ChevronDown className="w-4 h-4 text-gray-600" />
                ) : (
                  <ChevronRight className="w-4 h-4 text-gray-600" />
                )}
              </button>
            </Tooltip>
          )}
          {!hasChildren && <div className="w-4" />}

          <div className="flex-shrink-0">{getNodeIcon(node.type, node.status)}</div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-900 truncate">{node.name}</span>
              {node.duration !== undefined && (
                <Tooltip content={`Execution time: ${node.duration}ms`}>
                  <span className="text-xs text-gray-600 flex items-center ml-4">
                    <Clock className="w-3 h-3 mr-1" />
                    {node.duration}ms
                  </span>
                </Tooltip>
              )}
            </div>
            {node.data && (
              <Tooltip content="View detailed data payload for this node">
                <button
                  className="text-xs text-primary-600 hover:text-primary-700 mt-1 flex items-center"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedNodeData(node.data);
                    setShowDataDialog(true);
                  }}
                >
                  <Eye className="w-3 h-3 mr-1" />
                  View data
                </button>
              </Tooltip>
            )}
          </div>
        </div>

        {isExpanded && hasChildren && (
          <div className="mt-1">{node.children!.map((child) => renderTreeNode(child, level + 1))}</div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <Breadcrumb items={[
        { label: 'RAG System', path: '/rag' },
        { label: 'Trace Explorer' }
      ]} />

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Trace Explorer & Logs</h1>
          <p className="text-sm text-gray-600 mt-1">
            Detailed execution traces, agent decisions, and MCP server logs
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <Tooltip content="Export the selected trace as JSON for debugging or sharing">
            <button
              onClick={handleDownloadTrace}
              disabled={!selectedTrace}
              className="px-4 py-2 bg-white border border-gray-200 rounded-xl text-sm font-medium hover:bg-gray-50 flex items-center disabled:bg-gray-100 disabled:cursor-not-allowed"
            >
              <Download className="w-4 h-4 mr-2" />
              Export JSON
            </button>
          </Tooltip>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-6">
        {/* Trace List */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
            <Tooltip content="Search traces by ID, exam, or OE code">
              <div className="relative mb-3">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search traces..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9 pr-4 py-2 w-full bg-white border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-primary-500"
                />
              </div>
            </Tooltip>
            <p className="text-xs text-gray-600">{filteredTraces.length} trace{filteredTraces.length !== 1 ? 's' : ''} found</p>
          </div>

          <div className="divide-y divide-gray-100 max-h-[600px] overflow-y-auto">
            {filteredTraces.length === 0 && (
              <EmptyState
                icon={Search}
                title="No Traces Found"
                description={`No traces match "${searchTerm}". Try a different search term.`}
                action={{
                  label: 'Clear Search',
                  onClick: () => setSearchTerm(''),
                }}
              />
            )}
            {filteredTraces.map((trace) => (
              <Tooltip
                key={trace.traceId}
                content={`Click to view detailed trace for ${trace.traceId}`}
              >
                <div
                  className={`p-4 cursor-pointer transition-colors ${
                    selectedTrace === trace.traceId
                      ? 'bg-primary-50 border-l-4 border-l-primary-600'
                      : 'hover:bg-gray-50'
                  }`}
                  onClick={() => setSelectedTrace(trace.traceId)}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-mono text-gray-900">{trace.traceId}</span>
                    <StatusBadge status={trace.status} />
                  </div>
                  <div className="flex items-center space-x-2 text-xs text-gray-600">
                    <Tooltip content={`Exam: ${trace.exam}`}>
                      <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded">{trace.exam}</span>
                    </Tooltip>
                    <Tooltip content={`Learning Objective: ${trace.oeCode}`}>
                      <span>{trace.oeCode}</span>
                    </Tooltip>
                  </div>
                  <div className="flex items-center space-x-4 mt-2 text-xs text-gray-500">
                    <Tooltip content={`Total execution time: ${trace.duration}ms`}>
                      <span className="flex items-center">
                        <Clock className="w-3 h-3 mr-1" />
                        {trace.duration}ms
                      </span>
                    </Tooltip>
                    <Tooltip content={`Total cost: $${trace.cost.toFixed(3)}`}>
                      <span className="flex items-center">
                        <DollarSign className="w-3 h-3 mr-1" />
                        ${trace.cost.toFixed(3)}
                      </span>
                    </Tooltip>
                  </div>
                </div>
              </Tooltip>
            ))}
          </div>
        </div>

        {/* Trace Details */}
        <div className="col-span-2 space-y-6">
          {selectedTraceData && (
            <>
              {/* Summary */}
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Trace Summary</h3>
                <div className="grid grid-cols-4 gap-4">
                  <Tooltip content="Unique trace identifier for this execution">
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Trace ID</p>
                      <p className="text-sm font-mono text-gray-900">{selectedTraceData.traceId}</p>
                    </div>
                  </Tooltip>
                  <Tooltip content="Exam and learning objective code">
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Exam / OE Code</p>
                      <p className="text-sm font-medium text-gray-900">
                        {selectedTraceData.exam} / {selectedTraceData.oeCode}
                      </p>
                    </div>
                  </Tooltip>
                  <Tooltip content="Total time from start to finish">
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Duration</p>
                      <p className="text-sm font-medium text-gray-900">{selectedTraceData.duration}ms</p>
                    </div>
                  </Tooltip>
                  <Tooltip content="Total cost including all API calls (Claude + Cohere)">
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Cost</p>
                      <p className="text-sm font-medium text-gray-900">${selectedTraceData.cost.toFixed(3)}</p>
                    </div>
                  </Tooltip>
                  <Tooltip content="When this execution started">
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Timestamp</p>
                      <p className="text-sm font-medium text-gray-900">{selectedTraceData.timestamp}</p>
                    </div>
                  </Tooltip>
                  <Tooltip content="Total tokens consumed by LLM calls">
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Tokens Used</p>
                      <p className="text-sm font-medium text-gray-900">
                        {selectedTraceData.tokensUsed.toLocaleString()}
                      </p>
                    </div>
                  </Tooltip>
                  <Tooltip content="Number of questions successfully generated">
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Questions</p>
                      <p className="text-sm font-medium text-gray-900">
                        {selectedTraceData.questionsGenerated} generated
                      </p>
                    </div>
                  </Tooltip>
                  <Tooltip content="Final execution status">
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Status</p>
                      <StatusBadge status={selectedTraceData.status} />
                    </div>
                  </Tooltip>
                </div>
              </div>

              {/* View Modes */}
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-900">Execution Trace</h3>
                    <div className="flex items-center space-x-2">
                      <Tooltip content="View trace as an expandable tree hierarchy showing all stages, MCP servers, and API calls">
                        <button
                          onClick={() => setViewMode('tree')}
                          className={`px-3 py-1.5 text-xs font-medium rounded-lg ${
                            viewMode === 'tree'
                              ? 'bg-primary-600 text-white'
                              : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50'
                          }`}
                        >
                          Tree View
                        </button>
                      </Tooltip>
                      <Tooltip content="View trace as a timeline (coming soon)">
                        <button
                          onClick={() => setViewMode('timeline')}
                          className={`px-3 py-1.5 text-xs font-medium rounded-lg ${
                            viewMode === 'timeline'
                              ? 'bg-primary-600 text-white'
                              : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50'
                          }`}
                        >
                          Timeline
                        </button>
                      </Tooltip>
                      <Tooltip content="View raw JSON trace data for debugging">
                        <button
                          onClick={() => setViewMode('raw')}
                          className={`px-3 py-1.5 text-xs font-medium rounded-lg ${
                            viewMode === 'raw'
                              ? 'bg-primary-600 text-white'
                              : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50'
                          }`}
                        >
                          <Code className="w-4 h-4" />
                        </button>
                      </Tooltip>
                    </div>
                  </div>
                </div>

                <div className="p-6 max-h-[600px] overflow-y-auto">
                  {viewMode === 'tree' && renderTreeNode(traceData)}
                  {viewMode === 'timeline' && (
                    <EmptyState
                      icon={Clock}
                      title="Timeline View Coming Soon"
                      description="Timeline visualization will show execution flow over time with parallel operations"
                    />
                  )}
                  {viewMode === 'raw' && (
                    <pre className="text-xs bg-gray-900 text-green-400 p-4 rounded-lg overflow-x-auto font-mono">
                      {JSON.stringify(traceData, null, 2)}
                    </pre>
                  )}
                </div>
              </div>
            </>
          )}

          {!selectedTraceData && (
            <EmptyState
              icon={Search}
              title="No Trace Selected"
              description="Select a trace from the list on the left to view detailed execution logs, agent decisions, and MCP server calls"
            />
          )}
        </div>
      </div>

      {/* Data Dialog */}
      {showDataDialog && selectedNodeData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[80vh] overflow-hidden flex flex-col">
            <div className="px-6 py-4 border-b border-gray-200 bg-gray-50 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Node Data</h3>
              <button
                onClick={() => {
                  setShowDataDialog(false);
                  setSelectedNodeData(null);
                }}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <Eye className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto flex-1">
              <pre className="text-xs bg-gray-900 text-green-400 p-4 rounded-lg overflow-x-auto font-mono">
                {JSON.stringify(selectedNodeData, null, 2)}
              </pre>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-end">
              <button
                onClick={() => {
                  setShowDataDialog(false);
                  setSelectedNodeData(null);
                }}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
